import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, ArrowLeft } from "@phosphor-icons/react";
import { getCheckoutStatus } from "@/lib/billing";

/**
 * Polled success landing page. Stripe redirects here with ?session_id=...
 * We poll our backend which in turn asks Stripe and mirrors state into our
 * DB idempotently via rpc_apply_stripe_payment_result.
 */
const MAX_ATTEMPTS = 6;
const INTERVAL_MS = 2000;

export default function ParentBillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | failed | expired
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    async function tick() {
      try {
        const res = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        setDetails(res);
        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.status === "expired") {
          setState("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        setTimeout(tick, INTERVAL_MS);
      } catch (err) {
        if (!cancelled) {
          setState("failed");
          setDetails({ error: err.message });
        }
      }
    }
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="parent-billing-success" data-state={state} className="space-y-5">
      <Link
        to="/parent/billing"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <ArrowLeft size={16} weight="bold" />
        Back to billing
      </Link>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Payment
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">
          {state === "checking" && "Checking your payment…"}
          {state === "paid" && "Payment received"}
          {state === "pending" && "Payment still processing"}
          {state === "failed" && "Couldn’t confirm payment"}
          {state === "expired" && "Checkout session expired"}
        </h1>
      </header>

      <section
        className={`rounded-2xl border p-6 ${
          state === "paid"
            ? "border-primary/30 bg-primary/5"
            : state === "failed" || state === "expired"
              ? "border-[#C6851E]/40 bg-[#C6851E]/5"
              : "border-secondary/50 bg-secondary/20"
        }`}
      >
        <div className="flex items-start gap-4">
          {state === "paid" ? (
            <CheckCircle size={28} weight="duotone" className="mt-0.5 text-primary-hover" />
          ) : state === "failed" || state === "expired" ? (
            <XCircle size={28} weight="duotone" className="mt-0.5 text-[#C6851E]" />
          ) : (
            <div className="mt-1 h-6 w-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          )}
          <div className="min-w-0">
            {state === "paid" && (
              <p className="text-sm leading-relaxed text-neutral-800">
                Thanks — we received your payment. Your invoice has been marked
                paid and a receipt should arrive in your email shortly.
              </p>
            )}
            {state === "pending" && (
              <p className="text-sm leading-relaxed text-neutral-800">
                Your payment is still being processed by Stripe. You can safely
                leave this page — we'll update the invoice automatically once
                it clears.
              </p>
            )}
            {state === "failed" && (
              <p className="text-sm leading-relaxed text-neutral-800">
                We couldn't confirm the payment. If you were charged, it will
                auto-reconcile within a few minutes. Otherwise please try again
                from the invoice.
              </p>
            )}
            {state === "expired" && (
              <p className="text-sm leading-relaxed text-neutral-800">
                This checkout session is no longer valid. Please start a new
                payment from the invoice.
              </p>
            )}
            {details?.amount_total_cents ? (
              <p className="mt-3 text-xs text-neutral-500">
                Amount: {(details.amount_total_cents / 100).toFixed(2)}{" "}
                {(details.currency || "usd").toUpperCase()}
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
